<template>
  <div class="assets-view d-flex flex-column">
    <v-alert
      v-if="isLoading"
      type="info"
      dense
      outlined
      class="mb-2 flex-shrink-0"
    >
      <div class="d-flex align-center">
        <v-progress-circular
          indeterminate
          size="18"
          width="2"
          color="info"
          class="mr-2"
        />
        <span v-if="syncProgress.total > 0">
          Building asset table... ({{ syncProgress.loaded }} /
          {{ syncProgress.total }} twins loaded)
        </span>
        <span v-else> Loading data... </span>
      </div>
    </v-alert>

    <div
      v-if="hasActiveFilters"
      class="d-flex align-center mb-2 ml-4 flex-shrink-0"
    >
      <span class="text-caption mr-2">
        {{ filteredRows.length }} of {{ rows.length }} rows
      </span>
      <v-btn x-small text @click="clearAllFilters">
        <v-icon x-small left color="secondary">mdi-filter-remove</v-icon>
        Clear all filters
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredRows"
      :items-per-page="50"
      :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
      dense
      multi-sort
      fixed-header
      height="calc(100vh - 200px)"
      class="assets-table"
    >
      <template v-for="header in headers" v-slot:[headerSlot(header)]="{}">
        <column-filter
          :key="header.value"
          :label="header.text"
          :options="columnOptions(header.value)"
          :value="columnFilters[header.value]"
          @input="setFilter(header.value, $event)"
        />
      </template>
      <template v-for="header in headers" v-slot:[itemSlot(header)]="{ value }">
        <v-tooltip
          :key="header.value"
          bottom
          :disabled="!value"
          open-delay="400"
        >
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="cell-text">{{ value }}</span>
          </template>
          <span>{{ value }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import ColumnFilter from '@/components/ColumnFilter.vue'
import { observes } from '@/vue-wob-cache/vue-wob-observes'

// Columns that filter by individual instance label
const instanceFilterColumns = new Set([
  'realestate',
  'building',
  'level',
  'room',
])

export default {
  name: 'AssetsView',

  // The asset table is the one selection we have no evidence is in regular
  // use. Until this view is mounted the cache does not build it at all.
  mixins: [observes('assetPaths')],

  components: { ColumnFilter },
  data: () => ({
    headers: [
      {
        text: 'Realestate',
        value: 'realestate',
        sortable: false,
        width: '180px',
      },
      { text: 'Building', value: 'building', sortable: false },
      { text: 'Level', value: 'level', sortable: false },
      { text: 'Room', value: 'room', sortable: false },
      { text: 'System', value: 'system', sortable: false },
      { text: 'Equipment', value: 'equipment', sortable: false },
      { text: 'Point', value: 'point', sortable: false },
    ],
    columnFilters: {
      realestate: [],
      building: [],
      level: [],
      room: [],
      system: [],
      equipment: [],
      point: [],
    },
  }),
  computed: {
    rows() {
      return this.$wob.assetPaths.rows
    },
    syncProgress() {
      return this.$wob.syncProgress
    },
    isLoading() {
      return this.rows.length === 0 && !this.$wob.isSynced
    },
    hasActiveFilters() {
      return Object.values(this.columnFilters).some((f) => f.length > 0)
    },
    filteredRows() {
      return this.rows.filter((row) => {
        return Object.keys(this.columnFilters).every((col) => {
          const filter = this.columnFilters[col]
          if (!filter || filter.length === 0) return true
          const key = this.filterKey(col)
          return filter.includes(row[key] || '')
        })
      })
    },
  },
  methods: {
    headerSlot(header) {
      return `header.${header.value}`
    },
    itemSlot(header) {
      return `item.${header.value}`
    },
    filterKey(column) {
      return instanceFilterColumns.has(column) ? column : column + 'Type'
    },
    setFilter(column, value) {
      this.columnFilters[column] = value
    },
    clearAllFilters() {
      Object.keys(this.columnFilters).forEach((col) => {
        this.columnFilters[col] = []
      })
    },
    columnOptions(column) {
      const key = this.filterKey(column)
      // Cascade: compute available options based on other active filters
      const otherFiltered = this.rows.filter((row) => {
        return Object.keys(this.columnFilters).every((col) => {
          if (col === column) return true
          const filter = this.columnFilters[col]
          if (!filter || filter.length === 0) return true
          const k = this.filterKey(col)
          return filter.includes(row[k] || '')
        })
      })
      const values = otherFiltered.map((row) => row[key]).filter((v) => !!v)
      return [...new Set(values)].sort()
    },
  },
}
</script>

<style scoped>
.assets-view {
  align-self: flex-start;
  width: 100%;
}
.assets-table >>> .v-data-table__wrapper {
  overflow-y: auto;
}
.assets-table >>> thead th {
  white-space: nowrap;
  vertical-align: middle !important;
}
.assets-table >>> tbody td {
  max-width: 200px;
}
.cell-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
